<template>
  <v-container fluid data-testid="decks-table">
    <v-row dense>
      <v-col cols="auto" v-if="!readonly">
        <span class="mr-2 subtitle-1">
          {{$t('decks')}}
        </span>
        <Icon
          icon="mdi-plus"
          iconColor="primary"
          :tooltipText="$t('creatingDeck')"
          :small="false"
          dataTestId="deck-create-button"
          @icon-clicked="openForm()"
          :disabled="!userAssignedClaim(UserClaims.InventoryManager)"/>
      </v-col>
      <v-col cols="auto" v-if="readonly">
        <DatePicker
          v-if="renderDate"
          :dateLabel="$t('asOf')"
          @date-picked="dateChosen"
        />
      </v-col>
      <v-col cols="auto" v-if="readonly">
        <v-btn
          :disabled="!applyDateButtonEnabled"
          class="ml-2 mt-2"
          color="primary"
          @click="applyDate"
        >
          {{ $t('apply') }}
        </v-btn>
      </v-col>
      <v-spacer/>
      <v-col align="right" class="mr-2">
        <Icon
        :small="false"
        icon="mdi-file-delimited-outline"
        iconColor="primary"
        :tooltipText="$t('downloadCSV')"
        @icon-clicked="downloadCsv"
        />
        <Icon
        v-if="readonly"
        :small="false"
        icon="mdi-download"
        iconColor="primary"
        class="mr-2"
        :tooltipText="$t('downloadPDF')"
        @icon-clicked="downloadPdf"
        />
        <ColumnAdjuster
        :propColumnSet="columnSet"
        @new-columns-selected="newColumnsSelected"/>
      </v-col>
    </v-row>
    <v-row v-if="readonly && showLoadsOnDeckDisclosure" dense>
      <v-col cols="auto">
        <span class="font-italic">
          {{ $t('loadsOnDeckDisclosure') }}
        </span>
      </v-col>
    </v-row>
    <v-row>
      <v-col cols="auto">
        <v-text-field
          v-if="consumptionMode != null"
          :label="$t('consumptionMode')"
          color="primary"
          :value="consumptionModeText"
          outlined
          dense
          readonly
        ></v-text-field>
      </v-col>
    </v-row>
    <v-row dense>
      <v-col>
        <DataTable
          class="mt-n8"
          :items.sync="decksForTable"
          :headers="headers"
          :showSearch="false"
          :no-data-text="$t('noDecksForLocation')"
          :customCells="customTableCells"
        >
          <template #status="{item}">
            <v-row align="center" justify="center" style="flex-wrap: nowrap;">
              <Icon
              :icon="statusIcon(item).icon"
              :iconColor="statusIcon(item).color"
              :tooltipColor="statusIcon(item).color"
              :tooltipText="statusIcon(item).tooltip"
              :small="false"/>

              <Icon
              v-if="item?.deckContents?.length > 0"
              elementId="deck-restricted-icon"
              icon="mdi-lock"
              iconColor="secondary"
              :tooltipText="$t('restricted')"
              :small="false"/>

              <Icon
              :icon="iconForConsumptionMode(item.consumptionMode)"
              :tooltipText="ConsumptionMode.fromInt(item.consumptionMode)"
              :small="false"
              iconColor="black"/>
            </v-row>
          </template>

          <template #on-deck-tons="{item}">
            <span v-if="item.consumptionMode !== ConsumptionMode.BoardFoot.value">
              {{tonStringFromPounds(item.onDeck)}}
            </span>
            <span v-else>
              {{ $t('notApplicable') }}
            </span>
          </template>

          <template #on-deck-mbf="{item}">
            <span v-if="item.consumptionMode === ConsumptionMode.BoardFoot.value">
              {{mbfStringFromBf(item.onDeck)}}
            </span>
            <span v-else>
              {{ $t('notApplicable') }}
            </span>
          </template>

          <template #name="{item}">
            <Icon
            v-if="item.isDefault"
            icon="mdi-star"
            iconColor="rgba(0,0,0,.87)"
            :tooltipText="getTooltip(item)"
            />
            {{item.name}}
          </template>

          <template #value="{item}">
            {{formatMoney(item.value)}}
          </template>

          <template #modified-at="{item}">
            <v-tooltip
              bottom
              :color="item.status === DeckStatus.Open.value ? 'success' : 'black'"
            >
              <template v-slot:activator="{ on, attrs }">
                <span
                  v-bind="attrs"
                  v-on="on"
                >
                  {{utcToLocalDate(item.openClose)}}
                </span>
              </template>
              <span class="subtitle-1 white--text">
                {{ modLabel(item) }}
              </span>
            </v-tooltip>
          </template>

          <template #actions="{item}">
            <Icon
            icon="mdi-backup-restore"
            iconColor="error"
            dataTestId="deck-edit-button"
            :tooltipText="$t('reset')"
            :disabled="item.status === DeckStatus.Archived.value || item.consumptionMode === ConsumptionMode.TicketBased.value || !userAssignedClaim(UserClaims.InventoryManager)"
            @icon-clicked="openDeckResetForm(item)"/>
            <Icon
            icon="mdi-pencil"
            iconColor="success"
            dataTestId="deck-edit-button"
            :tooltipText="$t('edit')"
            @icon-clicked="openForm(item)"
            :disabled="item.status == DeckStatus.Archived.value || !userAssignedClaim(UserClaims.InventoryManager)"/>
            <Icon
            v-if="item.status === DeckStatus.Archived.value || item.status === DeckStatus.Closed.value"
            :icon="archiveIcon(item).icon"
            iconColor="error"
            :tooltipText="archiveIcon(item).tooltip"
            @icon-clicked="archiveIcon(item).click()"
            :disabled="!userAssignedClaim(UserClaims.InventoryManager)"/>
          </template>

          <template #species="{item}">
            <span v-if="item.consumptionMode !== ConsumptionMode.BoardFoot.value">
              {{ $t('notApplicable') }}
            </span>
            <span v-else>
              {{ item.species }}
            </span>
          </template>

          <template #loadsOnDeck="{item}">
            <Icon
            v-if="item.consumptionMode === ConsumptionMode.WeightBased.value"
            icon="mdi-weight"
            :tooltipText="ConsumptionMode.fromInt(item.consumptionMode)"
            :small="false"
            iconColor="black"/>
            <Icon
            v-else-if="item.consumptionMode === ConsumptionMode.BoardFoot.value"
            icon="mdi-ruler-square"
            :tooltipText="ConsumptionMode.fromInt(item.consumptionMode)"
            :small="false"
            iconColor="black"/>
            <span v-else>
              {{ item.loadsOnDeck }}
            </span>
          </template>

          <template #body.append>
            <tr class="font-weight-bold" v-if="decks?.length > 0">
              <td v-for="header in headers" :key="header.value" :class="`text-${header.align}`">
                <span v-if="header.value === 'onDeckTons' && onDeckTotalTons.visible" >
                  {{ tonStringFromPounds(onDeckTotalTons.value) }}
                </span>
                <span v-else-if="header.value === 'onDeckMbf' && onDeckTotalMbf.visible">
                  {{ mbfStringFromBf(onDeckTotalMbf.value, 3) }}
                </span>
                <span v-else-if="header.value === 'value' && valueTotal.visible">
                  {{ formatMoney(valueTotal.value) }}
                </span>
                <span v-else-if="header.value === 'loadsOnDeck' && loadsOnDeckTotal.visible">
                  {{ loadsOnDeckTotal.value }}
                </span>
              </td>
            </tr>
          </template>
        </DataTable>
      </v-col>
    </v-row>
    <Dialog :stateId="dialogId" @dialog-closing="close">
      <DeckForm
        v-if="deckForm == true"
        :propDeck="focusedDeck"
        :location="location"
        width="900px"
        @close="close"
        @deck-mutated="deckMutated"
      />
      <DeckResetForm
        v-if="deckResetForm == true"
        :propDeck="focusedDeck"
        :location="location"
        @close="close"
        @deck-adjusted="refreshDecks"
      />
      <ConfirmDialog
        v-if="archiveDeckConfirmation"
        :title="archiveDeckConfirmationText.title"
        :body="archiveDeckConfirmationText.body"
        :confirmText="archiveDeckConfirmationText.button"
        @confirm="toggleDeckArchiveStatus(focusedDeck)"
        @cancel="close"
      />
    </Dialog>
  </v-container>
</template>

<script>
import { formatMoney, tonStringFromPounds, mbfStringFromBf } from '@/utils/NumericMutations.js'
import { utcToLocalDate } from '@/utils/DateFormatter.js'
import { deckHeaders } from '@/headers/Deck.js'
import { ConsumptionMode, DeckContentType, DeckStatus } from '@/utils/Enumerations.js'
import { mapActions } from 'vuex'
import { UserClaims } from '../../../utils/Enumerations'
import { userAssignedClaim } from '../../../utils/ClaimUtility'
import { generateCsvString } from '@/utils/CSVUtility'
import { generateDeckInventoryPDF } from '@/utils/pdf-formatters/DeckInventory.js'
import { saveAs } from 'file-saver'
import moment from 'moment'
import { sumBy } from 'lodash'
import { NumericConstants } from '@/utils/constants'

const sortByStatusThenName = (a, b) => a.status !== b.status ? a.status - b.status : a.name.localeCompare(b.name)

export default {
  name: 'Decks',

  props: {
    location: {
      type: Object,
      required: true
    },
    decks: {
      type: Array,
      required: true
    },
    consumptionMode: {
      type: Number,
      default: undefined
    },
    readonly: {
      type: Boolean,
      default: false
    },
    refreshFlag: {
      type: Boolean,
      default: false
    }
  },

  components: {
    Dialog: () => import('@/components/Dialog.vue'),
    DeckForm: () => import('./DeckForm.vue'),
    DeckResetForm: () => import('./DeckResetForm.vue'),
    DataTable: () => import('@/components/core/table/DataTable.vue'),
    DatePicker: () => import('@/components/helper/DatePicker.vue'),
    ColumnAdjuster: () => import('@/components/core/ColumnAdjuster.vue'),
    Icon: () => import('@/components/helper/Icon.vue'),
    ConfirmDialog: () => import('@/components/helper/ConfirmDialog.vue')
  },

  data: () => ({
    dialogId: `decks-${Math.floor(Math.random() * 65536).toString(16).padStart(4, '0')}`,
    focusedDeck: undefined,
    deckForm: false,
    deckResetForm: false,
    loading: false,
    applyDateButtonEnabled: false,
    archivedDeckHistories: [],
    allDecks: [],
    columnSet: undefined,
    asOfDate: new Date().toISOString(),
    archiveDeckConfirmation: false,
    UserClaims,
    DeckStatus,
    ConsumptionMode,
    renderDate: true,
    sortedDecks: []
  }),

  created () {
    this.initializeHeaders()

    this.sortedDecks = this.decks.sort(sortByStatusThenName)

    if (this.readonly) {
      this.getAllDecksForLocation()
    }
  },

  computed: {
    headers () {
      const columns = this.columnSet?.getColumns() ?? []
      const existsMbf = this.decksForTable.some(d => d.consumptionMode === ConsumptionMode.BoardFoot.value)
      const existsTons = this.decksForTable.some(d => d.consumptionMode !== ConsumptionMode.BoardFoot.value)
      return columns.filter(c => {
        return (c.value !== 'onDeckMbf' || existsMbf) && (c.value !== 'onDeckTons' || existsTons)
      })
    },

    consumptionModeText () {
      return this.consumptionModeFromInt(this.consumptionMode)
    },

    decksForTable () {
      return this.archivedDeckHistories.length > 0 ? this.archivedDeckHistories : this.sortedDecks
    },

    showLoadsOnDeckDisclosure () {
      const ticketBasedDecks = this.decksForTable.filter(d => d.consumptionMode === ConsumptionMode.TicketBased.value)
      if (ticketBasedDecks.length === 0) return false

      return moment(this.asOfDate).isBefore(moment('07/16/2025'))
    },

    customTableCells () {
      return [
        {
          slotName: 'status',
          value: 'status'
        },
        {
          slotName: 'name',
          value: 'name'
        },
        {
          slotName: 'species',
          value: 'species'
        },
        {
          slotName: 'on-deck-tons',
          value: 'onDeckTons'
        },
        {
          slotName: 'on-deck-mbf',
          value: 'onDeckMbf'
        },
        {
          slotName: 'value',
          value: 'value'
        },
        {
          slotName: 'actions',
          value: 'actions'
        },
        {
          slotName: 'modified-at',
          value: 'openClose'
        },
        {
          slotName: 'loadsOnDeck',
          value: 'loadsOnDeck'
        }
      ]
    },

    loadsOnDeckTotal () {
      return {
        visible: this.columnSet.columns.some(c => c.value === 'loadsOnDeck' && c.visible),
        value: sumBy(this.decksForTable, 'loadsOnDeck')
      }
    },
    valueTotal () {
      return {
        visible: this.columnSet.columns.some(c => c.value === 'value' && c.visible),
        value: sumBy(this.decksForTable, 'value')
      }
    },
    onDeckTotalTons () {
      return {
        visible: true,
        value: sumBy(this.decksForTable.filter(d => d.consumptionMode !== ConsumptionMode.BoardFoot.value), 'onDeck')
      }
    },
    onDeckTotalMbf () {
      return {
        visible: true,
        value: sumBy(this.decksForTable.filter(d => d.consumptionMode === ConsumptionMode.BoardFoot.value), 'onDeck')
      }
    },
    showContentType () {
      return !this.readonly
    },
    showModifiedAt () {
      return !this.readonly
    },
    showActions () {
      return !this.readonly
    },
    archiveDeckConfirmationText () {
      return this.focusedDeck.status === DeckStatus.Archived.value
        ? {
          title: this.$t('unarchiveDeck'),
          body: this.$t('unarchiveDeckBody', { deckName: this.focusedDeck?.name }),
          button: this.$t('unarchive')
        }
        : {
          title: this.$t('archiveDeck'),
          body: this.$t('archiveDeckBody', { deckName: this.focusedDeck?.name }),
          button: this.$t('archive')
        }
    }
  },

  watch: {
    decks: {
      handler (val, oldVal) {
        this.archivedDeckHistories = []
        this.sortedDecks = [...val].sort(sortByStatusThenName)
      }
    },

    async refreshFlag () {
      if (this.refreshFlag) {
        this.archivedDeckHistories = []
        await this.getAllDecksForLocation()
        this.applyDate()
      }
    },

    async location () {
      if (this.readonly) {
        this.archivedDeckHistories = []
        await this.getAllDecksForLocation()
        this.applyDate()
      }
    }
  },

  methods: {
    ...mapActions('dialog', ['openOrUpdateDialog', 'closeDialogsAtOrAbove']),
    ...mapActions('decks', ['getArchivedDeckHistory', 'patchDeck', 'fetchDecksForLocation']),
    formatMoney,
    tonStringFromPounds,
    mbfStringFromBf,
    utcToLocalDate,
    userAssignedClaim,

    consumptionModeFromInt (mode) {
      return ConsumptionMode.fromInt(mode)
    },

    close () {
      this.closeDialogsAtOrAbove(this.dialogId)
      this.deckForm = false
      this.deckResetForm = false
      this.focusedDeck = undefined
      this.archiveDeckConfirmation = false
    },

    deckMutated (mutatedDeck) {
      const index = this.sortedDecks.findIndex(d => d.deckId === mutatedDeck.deckId)

      if (index !== -1) {
        this.sortedDecks.splice(index, 1, mutatedDeck)
      }
      this.close()
      this.$emit('deck-mutated')
    },

    refreshDecks () {
      this.sortedDecks = []
      this.close()
      this.$emit('deck-mutated')
    },

    dateChosen (date) {
      if (moment(date).isAfter(moment(), 'day')) {
        this.applyDateButtonEnabled = false
        return
      }

      this.applyDateButtonEnabled = true
      this.asOfDate = date
    },

    async getAllDecksForLocation () {
      this.allDecks = await this.fetchDecksForLocation({
        locationId: this.location.locationId,
        includeLogs: this.decks[0]?.contentType === DeckContentType.Logs.value,
        includeByproducts: this.decks[0]?.contentType === DeckContentType.Byproducts.value,
        includeArchived: true,
        includeVolume: true
      })
    },

    async applyDate () {
      if (moment(this.asOfDate).isAfter(moment(), 'day')) {
        this.setSnackError(this.$t('invalidFutureDate'))
        return
      }

      this.applyDateButtonEnabled = false

      if (moment(this.asOfDate).isSame(moment(), 'day')) {
        this.archivedDeckHistories = []
        return
      }

      const decks = await this.getArchivedDeckHistory({
        locationId: this.location.locationId,
        contentType: this.decks[0]?.contentType ?? DeckContentType.Logs.value,
        sinceTime: this.asOfDate ? moment.utc(this.asOfDate).add(1, 'day').startOf('day').format() : undefined,
        untilTime: this.asOfDate ? moment.utc(this.asOfDate).add(1, 'day').endOf('day').format() : undefined
      })

      if (decks.length === 0) {
        this.setSnackError(this.$t('noArchivedDecksForDate'))
        this.renderDate = false
        this.asOfDate = new Date().toISOString()
        this.applyDateButtonEnabled = false
        this.archivedDeckHistories = []

        this.$nextTick(() => {
          this.renderDate = true
        })
        return
      }

      const deckDictionary = this.allDecks.reduce((acc, deck) => {
        acc[deck.name] = deck
        return acc
      }, {})

      this.archivedDeckHistories = decks.map(deck => {
        const originalDeck = deckDictionary[deck.deckName]

        return {
          ...originalDeck,
          name: deck.deckName,
          deckId: deck.deckId,
          loadsOnDeck: deck.loadsOnDeck ?? 0,
          onDeck: deck.onDeck ?? 0,
          value: deck.value ?? 0
        }
      }).sort(sortByStatusThenName)
    },

    openForm (deck) {
      this.close()
      this.focusedDeck = deck
      this.deckForm = true
      this.openOrUpdateDialog({ id: this.dialogId, width: '900' })
    },

    openDeckResetForm (deck) {
      this.close()
      this.focusedDeck = deck
      this.deckResetForm = true
      this.openOrUpdateDialog({ id: this.dialogId, width: '1800' })
    },

    getTooltip (deck) {
      return this.$t('defaultCategoryDeck', { category: DeckContentType.fromInt(deck.contentType) })
    },

    downloadCsv () {
      const csvFields = [
        { label: 'deck', value: deck => deck.name },
        { label: 'status', value: deck => DeckStatus.fromInt(deck.status) },
        {
          label: 'consumptionMode',
          value: deck => {
            switch (deck.consumptionMode) {
              case ConsumptionMode.BoardFoot.value:
                return 'BF'
              case ConsumptionMode.WeightBased.value:
                return 'W'
              case ConsumptionMode.TicketBased.value:
                return 'T'
              default:
                return 'W'
            }
          }
        },
        this.loadsOnDeckTotal.visible ? { label: 'loadsOnDeck', value: deck => deck.loadsOnDeck } : undefined,
        {
          label: 'mbf',
          value: deck => {
            return deck.consumptionMode === ConsumptionMode.BoardFoot.value
              ? (deck.onDeck / NumericConstants.MbfDivisor).toFixed(3)
              : this.$t('notApplicable')
          }
        },
        {
          label: 'tons',
          value: deck => {
            return deck.consumptionMode === ConsumptionMode.BoardFoot.value
              ? this.$t('notApplicable')
              : (deck.onDeck / NumericConstants.ShortTonDivisor).toFixed(3)
          }
        },
        {
          label: 'pounds',
          value: deck => {
            return deck.consumptionMode === ConsumptionMode.BoardFoot.value
              ? this.$t('notApplicable')
              : deck.onDeck
          }
        },
        this.valueTotal.visible ? { label: 'value', value: deck => deck.value } : undefined
      ].filter(f => f !== undefined)

      const csvString = generateCsvString(this.decksForTable, csvFields)
      const blob = new Blob([csvString], { type: 'text/plain;charset=utf-8' })
      const formattedLocationName = this.location.name.replaceAll(/\s/g, '_')
      saveAs(blob, `${formattedLocationName}_Decks_${moment.utc(this.asOfDate).format('YYYYMMDD')}.csv`)
    },

    initializeHeaders () {
      const base = deckHeaders()
      base.columns = this.readonly
        ? base.columns.filter(bh => bh.value !== 'actions' && bh.value !== 'openClose' && bh.value !== 'contentType')
        : base.columns

      this.columnSet = base
    },

    newColumnsSelected (columnSet) {
      this.columnSet = columnSet
    },

    downloadPdf () {
      generateDeckInventoryPDF(this.decksForTable, this.location.name, this.asOfDate)
    },
    archiveDeckClicked (deck) {
      this.close()
      if (deck.onDeck !== 0) {
        this.setSnackError(this.$t('cannotArchiveDeckWithOnDeck'))
        return
      }
      if (deck.isDefault) {
        this.setSnackError(this.$t('cannotArchiveDefaultDeck'))
        return
      }

      this.archiveDeckConfirmation = true
      this.focusedDeck = deck
      this.openOrUpdateDialog({ id: this.dialogId, width: '400' })
    },

    unarchiveDeckClicked (deck) {
      this.close()
      this.archiveDeckConfirmation = true
      this.focusedDeck = deck
      this.openOrUpdateDialog({ id: this.dialogId, width: '400' })
    },

    async toggleDeckArchiveStatus (deck) {
      const { deckId } = deck
      const { locationId } = this.location
      const body = [{
        op: 'add',
        path: '/status',
        value: deck.status === DeckStatus.Archived.value
          ? DeckStatus.Closed.value
          : DeckStatus.Archived.value
      }]
      await this.patchDeck({
        locationId,
        deckId,
        body
      })
      deck.status = body[0].value
      const index = this.sortedDecks.findIndex(d => d.deckId === deck.deckId)
      if (index === -1) {
        this.refreshDecks()
        return
      }
      this.sortedDecks.splice(index, 1, deck)

      this.close()
      this.$emit('deck-mutated')
    },

    statusIcon (deck) {
      switch (deck.status) {
        case DeckStatus.Open.value:
          return {
            icon: 'mdi-checkbox-marked-circle',
            color: 'success',
            tooltip: this.$t('open')
          }
        case DeckStatus.Closed.value:
          return {
            icon: 'mdi-close',
            color: 'black',
            tooltip: this.$t('closed')
          }
        case DeckStatus.Archived.value:
          return {
            icon: 'mdi-archive',
            color: 'error',
            tooltip: this.$t('archived')
          }
        default:
          return {
            icon: 'mdi-checkbox-marked-circle',
            color: 'success',
            tooltip: this.$t('open')
          }
      }
    },

    archiveIcon (deck) {
      return deck.status === DeckStatus.Archived.value
        ? {
          icon: 'mdi-archive-off-outline',
          tooltip: this.$t('unarchiveDeck'),
          click: () => this.unarchiveDeckClicked(deck)
        }
        : {
          icon: 'mdi-archive',
          tooltip: this.$t('archiveDeck'),
          click: () => this.archiveDeckClicked(deck)
        }
    },

    modLabel (deck) {
      switch (deck.status) {
        case DeckStatus.Open.value:
          return this.$t('openedAt')
        case DeckStatus.Closed.value:
          return this.$t('closedAt')
        case DeckStatus.Archived.value:
          return this.$t('archivedAt')
      }
    },

    iconForConsumptionMode (consumptionMode) {
      return consumptionMode === ConsumptionMode.TicketBased.value
        ? 'mdi-clipboard-outline'
        : consumptionMode === ConsumptionMode.WeightBased.value
          ? 'mdi-weight'
          : consumptionMode === ConsumptionMode.BoardFoot.value
            ? 'mdi-ruler-square'
            : ''
    },

    unitDivisorForConsumptionMode (consumptionMode) {
      return consumptionMode === ConsumptionMode.BoardFoot.value
        ? NumericConstants.MbfDivisor
        : NumericConstants.ShortTonDivisor
    }
  }
}
</script>
